import React from 'react';
import { Box, Chip, Tooltip, Link, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { VisibilityOutlined as VisibilityIcon } from '@mui/icons-material';
import { BaseTable, TableColumn } from '../../common/Table';
import { ActionButton } from '../../common/Table/Table.styles';
import { ProductHistoryListResponseDto } from '../../../api/models/ProductHistoryListResponseDto';
import {
  EVENT_TYPE_COLORS,
  getEventTypeLabelForListItem,
  formatHistoryDate,
  isAccountSecurityEventType,
} from '../../../utils/productHistory.utils';
import {
  useRelatedEntityNavigation,
  type RelatedEntityNavigationExtra,
} from '../../../hooks/useRelatedEntityNavigation';

export interface ProductMovementHistoryTableProps {
  data: ProductHistoryListResponseDto[];
  onViewDetails: (id: number) => void;
}

const relatedEntityLinkSx = (theme: Theme) => ({
  textDecoration: 'none',
  fontWeight: 500,
  color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main,
  '&:hover': {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
});

/** Italic caption used when a document-level event has no product row. */
function DocumentLevelProductPlaceholder({
  title,
  summary,
}: {
  title: string;
  summary: string;
}): React.ReactElement {
  return (
    <Box>
      <Typography
        variant="body2"
        fontWeight={500}
        color="text.secondary"
        sx={{ fontStyle: 'italic' }}
      >
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {summary}
      </Typography>
    </Box>
  );
}

const ProductMovementHistoryTable: React.FC<ProductMovementHistoryTableProps> = ({
  data,
  onViewDetails,
}) => {
  const { navigateToRelatedEntity } = useRelatedEntityNavigation();

  const getEventTypeColor = (eventType: ProductHistoryListResponseDto.eventType): string => {
    return EVENT_TYPE_COLORS[eventType] || '#6B7280';
  };

  const handleRelatedEntityClick = (
    e: React.MouseEvent,
    type: string | undefined,
    id: number | undefined,
    code: string | undefined,
    extra?: RelatedEntityNavigationExtra,
  ) => {
    e.stopPropagation();
    navigateToRelatedEntity(type, id, code, extra);
  };

  const renderRelatedEntity = (item: ProductHistoryListResponseDto) => {
    const safetyStockProduct = item.relatedEntityType === 'SAFETY_STOCK' ? item.product : undefined;

    if (item.transferCode) {
      const documentEntityType =
        item.relatedEntityType === 'INVENTORY_REQUEST' ? 'INVENTORY_REQUEST' : 'INVENTORY_TRANSFER';
      return (
        <Link
          component="button"
          variant="body2"
          onClick={(e) =>
            handleRelatedEntityClick(e, documentEntityType, item.relatedEntityId, item.transferCode)
          }
          sx={relatedEntityLinkSx}
        >
          {item.transferCode}
        </Link>
      );
    }
    if (item.orderCode) {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={(e) =>
            handleRelatedEntityClick(e, 'SALES_ORDER', item.relatedEntityId, item.orderCode)
          }
          sx={relatedEntityLinkSx}
        >
          {item.orderCode}
        </Link>
      );
    }
    if (item.recordCode) {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={(e) =>
            handleRelatedEntityClick(e, 'INVENTORY_RECORD', item.relatedEntityId, item.recordCode)
          }
          sx={relatedEntityLinkSx}
        >
          {item.recordCode}
        </Link>
      );
    }
    if (safetyStockProduct) {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={(e) =>
            handleRelatedEntityClick(e, 'SAFETY_STOCK', item.relatedEntityId, undefined, {
              productId: safetyStockProduct.id,
              locationId: item.location?.id,
              productCode: safetyStockProduct.code,
            })
          }
          sx={relatedEntityLinkSx}
        >
          Stock de seguridad
        </Link>
      );
    }
    if (item.relatedEntityType === 'SAFETY_STOCK_BULK') {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={(e) =>
            handleRelatedEntityClick(e, 'SAFETY_STOCK_BULK', item.relatedEntityId, undefined, {
              locationId: item.location?.id,
            })
          }
          sx={relatedEntityLinkSx}
        >
          Edición personalizada
        </Link>
      );
    }
    if (item.relatedEntityType === 'SAFETY_STOCK_BULK_SELECTION') {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={(e) =>
            handleRelatedEntityClick(
              e,
              'SAFETY_STOCK_BULK_SELECTION',
              item.relatedEntityId,
              undefined,
            )
          }
          sx={relatedEntityLinkSx}
        >
          Por selección
        </Link>
      );
    }
    if (item.relatedEntityType === 'PHYSICAL_COUNT') {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={(e) =>
            handleRelatedEntityClick(e, 'PHYSICAL_COUNT', item.relatedEntityId, undefined)
          }
          sx={relatedEntityLinkSx}
        >
          Conteo físico
        </Link>
      );
    }
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  };

  const columns: TableColumn<ProductHistoryListResponseDto>[] = [
    {
      id: 'eventType',
      label: 'Tipo de Evento',
      headerStyle: { maxWidth: 230, width: 230 },
      cellStyle: { maxWidth: 230, width: 230 },
      render: (item) => (
        <Chip
          label={getEventTypeLabelForListItem(item)}
          size="small"
          sx={{
            backgroundColor: getEventTypeColor(item.eventType),
            color: 'white',
            fontWeight: 500,
            maxWidth: '100%',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
      ),
    },
    {
      id: 'performedAt',
      label: 'Fecha/Hora',
      render: (item) => (
        <Typography variant="body2" color="text.primary">
          {formatHistoryDate(item.performedAt)}
        </Typography>
      ),
    },
    {
      id: 'product',
      label: 'Producto',
      render: (item) =>
        item.product ? (
          <Box>
            <Typography variant="body2" fontWeight={500} color="text.primary">
              {item.product.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              fontFamily={
                item.warehouseProduct?.productCodeUnique || item.productCodeUnique
                  ? 'monospace'
                  : 'inherit'
              }
            >
              {item.warehouseProduct?.productCodeUnique ||
                item.productCodeUnique ||
                item.product.code}
            </Typography>
          </Box>
        ) : (item.eventType === 'PRODUCT_CREATED' || item.eventType === 'PRODUCT_DELETED') &&
          item.changeSummary != null &&
          item.changeSummary !== '' ? (
          <DocumentLevelProductPlaceholder
            title={item.eventType === 'PRODUCT_DELETED' ? '(Producto eliminado)' : '(Producto)'}
            summary={item.changeSummary}
          />
        ) : (item.eventType === 'CUSTOMER_CREATED' ||
            item.eventType === 'CUSTOMER_UPDATED' ||
            item.eventType === 'CUSTOMER_DELETED') &&
          item.changeSummary != null &&
          item.changeSummary !== '' ? (
          <DocumentLevelProductPlaceholder
            title={item.eventType === 'CUSTOMER_DELETED' ? '(Cliente eliminado)' : '(Cliente)'}
            summary={item.changeSummary}
          />
        ) : isAccountSecurityEventType(item.eventType) &&
          item.changeSummary != null &&
          item.changeSummary !== '' ? (
          <DocumentLevelProductPlaceholder
            title="(Seguridad de cuenta)"
            summary={item.changeSummary}
          />
        ) : item.eventType === 'ORDER_DELIVERY_UPDATED' &&
          item.changeSummary != null &&
          item.changeSummary !== '' ? (
          <DocumentLevelProductPlaceholder
            title="(Entrega al cliente)"
            summary={item.changeSummary}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      id: 'location',
      label: 'Ubicación',
      render: (item) =>
        item.location ? (
          <Typography variant="body2" color="text.primary">
            {item.location.name}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      id: 'performedBy',
      label: 'Usuario',
      render: (item) =>
        item.performedBy ? (
          <Typography variant="body2" color="text.primary">
            {item.performedBy.firstName} {item.performedBy.lastName}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      id: 'relatedEntity',
      label: 'Entidad Relacionada',
      render: renderRelatedEntity,
    },
    {
      id: 'changeSummary',
      label: 'Resumen de Cambios',
      render: (item) =>
        item.changeSummary ? (
          <Tooltip title={item.changeSummary}>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.changeSummary}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
  ];

  return (
    <BaseTable<ProductHistoryListResponseDto>
      items={data}
      columns={columns}
      getItemId={(item) => item.id}
      emptyStateTitle="No se encontraron registros"
      showActions
      renderActions={(row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Ver detalles" arrow>
            <ActionButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(row.id);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </ActionButton>
          </Tooltip>
        </Box>
      )}
    />
  );
};

export default ProductMovementHistoryTable;
